import { observer } from 'mobx-react-lite';
import { useEffect, useState, useRef } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import Popup from '../../../../../../../components/common-components/pop-up/Popup';
import { FilePlus } from 'lucide-react';
import { useStore } from '../../../../../../../hooks/useStore';
import FormPopupFooter from '../../../../../../../components/form-components/common-components/FormPopupFooter';
import { FormikTextField } from '../../../../../../../components/form-components/common-components/text-field/FormikTextField';
import { FormikDropdown } from '../../../../../../../components/form-components/common-components/dropdown/FormikDropdown';
import styles from './NewRowManuel.module.css';
import {
  IHospitalRecordParam,
  NewRowManuelFormValues,
  getNewRowManuelInitialValues,
  TAG_COLOR_OPTIONS,
} from '../../../../../../../models/hospital/HospitalModel';
import { NewRowManuelSchema } from '../../../../../../../schemas/NewRowManuelSchema';
import { toast } from 'react-toastify';
import { RsValue } from '../../../../../../../models/base/BaseModel';
import { hospitalRecordFieldIcons as HRI } from '../../../hospitalRecordFieldIcons';

const ColorChangeHandler = ({
  getNextLabelForColor,
  handleTagColorChange,
}: {
  getNextLabelForColor: (color: string) => number;
  handleTagColorChange: (color: string) => void;
}) => {
  const { values, setFieldValue } = useFormikContext<NewRowManuelFormValues>();
  const prevColorRef = useRef<string>(values.color);

  useEffect(() => {
    if (values.color !== prevColorRef.current) {
      handleTagColorChange(values.color);
      setFieldValue('labelNo', getNextLabelForColor(values.color));
      prevColorRef.current = values.color;
    }
  }, [values.color, setFieldValue, getNextLabelForColor, handleTagColorChange]);

  return null;
};
const NewRowManuel = () => {
  const { popupStore, hospitalFilesStore, authStore } = useStore();
  const [selectedTagColor, setSelectedTagColor] = useState<string>('green');
  const formikRef = useRef<any>(null);

  const getNextLabelForColor = (color: string): number => {
    const colorItem = hospitalFilesStore.tagColors?.find(
      x => x.color?.toLowerCase() === color?.toLowerCase()
    );
    if (colorItem && typeof colorItem.sequence === 'number') {
      return colorItem.sequence;
    }
    return 0;
  };

  useEffect(() => {
    const savedTagColor = localStorage.getItem('selectedTagColor');
    if (savedTagColor) {
      setSelectedTagColor(savedTagColor);
    }
    if (!hospitalFilesStore.tagColors?.length && hospitalFilesStore.selectedTag?.id) {
      hospitalFilesStore.getTagColors();
    }
  }, []);

  useEffect(() => {
    // When tag colors or selected color changes, auto set the next label number
    if (formikRef.current && popupStore.isNewManuelRowOpen) {
      formikRef.current.setFieldValue('labelNo', getNextLabelForColor(selectedTagColor));
    }
  }, [hospitalFilesStore.tagColors, selectedTagColor, popupStore.isNewManuelRowOpen]);

  const handleTagColorChange = (color: string) => {
    setSelectedTagColor(color);
    localStorage.setItem('selectedTagColor', color);
  };

  const handleSubmit = async (values: NewRowManuelFormValues) => {
    try {
      if (!hospitalFilesStore.selectedHospital) {
        toast.error('Lütfen bir hastane seçiniz');
        return;
      }

      if (!authStore.user) {
        toast.error('Kullanıcı bilgisi bulunamadı');
        return;
      }

      const newRecord: IHospitalRecordParam = {
        tagId: hospitalFilesStore.selectedTag?.id,
        tagSequence: Number(values.labelNo ?? 0),
        tagColor: selectedTagColor,
        biomedicalDeviceDescription: values.deviceDefinition,
        brand: values.brand,
        model: values.model,
        serialNumber: values.serialNumber,
        identificationNumber: values.tagNumber,
        location: values.location,
        responsiblePerson: values.owner,
        note: values.notes,
        hospitalId: hospitalFilesStore.selectedHospital.id,
        userId: authStore.user.id,
        color: values.color,
      };

      const response: RsValue<any> = await hospitalFilesStore.newRowManuel(newRecord);
      if (!response.error) {
        hospitalFilesStore.refreshSelectedTag();
        popupStore.setIsNewManuelRowOpen(false);
        toast.success('Kayıt başarıyla eklendi', {
          position: 'top-right',
          autoClose: 3000,
        });
      } else {
        toast.error('Veri eklenirken bir hata meydana geldi. ' + response.message);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Kayıt eklenirken bir hata oluştu', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const handleSuggestionSearch = async (fieldName: string, searchTerm: string) => {
    try {
      if (!hospitalFilesStore || !fieldName || !searchTerm) {
        return [];
      }
      return await hospitalFilesStore.searchFieldSuggestions(fieldName, searchTerm);
    } catch (error) {
      console.error('Suggestion search error:', error);
      return [];
    }
  };

  if (!popupStore.isNewManuelRowOpen) {
    return null;
  }

  const handleClose = () => {
    popupStore.setIsNewManuelRowOpen(false);
  };

  return (
    <Formik
      key={`form-${popupStore.isNewManuelRowOpen}`}
      innerRef={formikRef}
      initialValues={getNewRowManuelInitialValues(selectedTagColor, getNextLabelForColor)}
      validationSchema={NewRowManuelSchema}
      onSubmit={handleSubmit}
      enableReinitialize={false}
    >
      {() => (
        <Popup
          isOpen={popupStore.isNewManuelRowOpen}
          onClose={handleClose}
          title="Yeni Veri Girişi"
          description="Manuel olarak yeni veri kaydı oluşturun"
          variant="success"
          icon={<FilePlus size={22} strokeWidth={2} />}
          footer={() => <FormPopupFooter onClose={handleClose} />}
        >
          <div className={styles.container}>
            <Form className={styles.form}>
              <ColorChangeHandler
                getNextLabelForColor={getNextLabelForColor}
                handleTagColorChange={handleTagColorChange}
              />
              <div className={styles.inputContainer}>
                <FormikDropdown
                  name="color"
                  label="Etiket Rengi"
                  placeholder="Etiket rengi seçiniz"
                  options={TAG_COLOR_OPTIONS}
                  startIcon={HRI.color}
                />
              </div>
              <div className={styles.inputContainer}>
                <FormikTextField
                  name="labelNo"
                  label="Etiket No"
                  placeholder="Etiket no giriniz"
                  type="number"
                  startIcon={HRI.labelNo}
                />
              </div>
              <div className={styles.inputContainer}>
                <FormikTextField
                  name="deviceDefinition"
                  label="Biyomedikal Cihaz Tanımı"
                  placeholder="Cihaz tanımını giriniz"
                  fieldToSearch="biomedicalDeviceDescription"
                  onSuggestionSearch={handleSuggestionSearch}
                  startIcon={HRI.deviceDefinition}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="brand"
                  label="Marka"
                  placeholder="Marka giriniz"
                  fieldToSearch="brand"
                  onSuggestionSearch={handleSuggestionSearch}
                  startIcon={HRI.brand}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="model"
                  label="Model"
                  placeholder="Model giriniz"
                  fieldToSearch="model"
                  onSuggestionSearch={handleSuggestionSearch}
                  startIcon={HRI.model}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="serialNumber"
                  label="Seri Numarası"
                  placeholder="Seri numarası giriniz"
                  startIcon={HRI.serialNumber}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="tagNumber"
                  label="Künye Numarası"
                  placeholder="Künye numarası giriniz"
                  startIcon={HRI.tagNumber}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="location"
                  label="Bulunduğu Yer"
                  placeholder="Bulunduğu yeri giriniz"
                  fieldToSearch="location"
                  onSuggestionSearch={handleSuggestionSearch}
                  startIcon={HRI.location}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="owner"
                  label="Zimmet Sahibi"
                  placeholder="Zimmet sahibini giriniz"
                  startIcon={HRI.owner}
                  // fieldToSearch="responsiblePerson"
                  // onSuggestionSearch={handleSuggestionSearch}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="notes"
                  label="Not"
                  placeholder="Not giriniz"
                  startIcon={HRI.notes}
                />
              </div>
            </Form>
          </div>
        </Popup>
      )}
    </Formik>
  );
};

export default observer(NewRowManuel);
